import apiClient from "./apiClient";
import { IDENTITY_SERVICE_BASE_URL } from "../constants";

const LAST_ACTIVITY_STORAGE_KEY = "oliveebLastActivityAt";
const SESSION_LOCK_STORAGE_KEY = "oliveebSessionLockedAt";
const SESSION_UNLOCK_STORAGE_KEY = "oliveebSessionUnlockedAt";
const ACTIVITY_BROADCAST_THROTTLE_MS = 1000;
const REFRESH_RETRY_DELAY_MS = 15 * 1000;
export const SESSION_IDLE_TIMEOUT_MINUTES = 30;

class TokenRefreshService {
  constructor() {
    this.refreshTimer = null;
    this.idleTimer = null;
    this.lockTimer = null;
    this.refreshEnabled = true;
    this.isLocked = false;
    this.isRefreshing = false;
    this.refreshPromise = null;
    this.onSessionExpired = null;
    this.onSessionLocked = null;
    this.onSessionUnlocked = null;
    this.onTokenRefreshed = null;
    this.idleTimeoutMs = SESSION_IDLE_TIMEOUT_MINUTES * 60 * 1000;
    this.lockGraceMs = 5 * 60 * 1000;
    this.refreshBufferMs = 2 * 60 * 1000;
    this.lockedAt = null;
    this.lastActivityTime = Date.now();
    this.lastActivityBroadcastTime = 0;
  }

  initialize(onSessionExpired, onTokenRefreshed, { enableRefresh = true, onSessionLocked, onSessionUnlocked } = {}) {
    this.cleanup();
    this.onSessionExpired = onSessionExpired;
    this.onSessionLocked = onSessionLocked;
    this.onSessionUnlocked = onSessionUnlocked;
    this.onTokenRefreshed = onTokenRefreshed;
    this.refreshEnabled = enableRefresh;
    this.isLocked = false;
    this.lockedAt = null;
    this.recordActivity({ forceBroadcast: true });
    this.trackActivity();
    if (this.refreshEnabled) {
      this.startRefreshTimer();
    }
  }

  trackActivity() {
    const update = () => this.recordActivity();
    const handleStorage = (event) => {
      if (event.key === LAST_ACTIVITY_STORAGE_KEY) {
        const sharedActivityTime = Number(event.newValue || 0);
        if (!Number.isFinite(sharedActivityTime) || sharedActivityTime <= this.lastActivityTime) return;
        this.lastActivityTime = sharedActivityTime;
        if (this.isLocked && (!this.lockedAt || sharedActivityTime > this.lockedAt)) {
          this.unlockSession({ broadcast: false });
          return;
        }
        this.startIdleTimer();
        return;
      }
      if (event.key === SESSION_LOCK_STORAGE_KEY) {
        const lockedAt = Number(event.newValue || 0);
        if (Number.isFinite(lockedAt) && lockedAt > 0) {
          if (this.getLastActivityTime() > lockedAt && this.isUserActive()) {
            this.unlockSession({ broadcast: true });
            return;
          }
          this.handleSessionLocked(lockedAt);
        }
        return;
      }
      if (event.key === SESSION_UNLOCK_STORAGE_KEY) {
        this.unlockSession({ broadcast: false });
      }
    };

    const activityEvents = [
      "click",
      "focus",
      "input",
      "keydown",
      "mousedown",
      "mousemove",
      "pointerdown",
      "scroll",
      "touchmove",
      "touchstart",
      "wheel",
    ];

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, update, { capture: true, passive: true });
    });
    window.addEventListener("storage", handleStorage);

    this.startIdleTimer();

    this.cleanupActivity = () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, update, { capture: true });
      });
      window.removeEventListener("storage", handleStorage);
    };
  }

  recordActivity({ forceBroadcast = false } = {}) {
    if (this.isLocked && !forceBroadcast) {
      return;
    }

    const now = Date.now();
    this.lastActivityTime = now;

    if (
      forceBroadcast ||
      now - this.lastActivityBroadcastTime >= ACTIVITY_BROADCAST_THROTTLE_MS
    ) {
      this.lastActivityBroadcastTime = now;
      localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(now));
    }

    this.startIdleTimer();
  }

  getLastActivityTime() {
    const sharedActivityTime = Number(localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY) || 0);
    return Math.max(
      this.lastActivityTime,
      Number.isFinite(sharedActivityTime) ? sharedActivityTime : 0,
    );
  }

  isUserActive() {
    return Date.now() - this.getLastActivityTime() < this.idleTimeoutMs;
  }

  startIdleTimer() {
    if (this.isLocked) {
      return;
    }

    this.clearIdleTimer();
    const remainingMs = Math.max(
      this.idleTimeoutMs - (Date.now() - this.getLastActivityTime()),
      0,
    );
    this.idleTimer = setTimeout(() => {
      if (this.isUserActive()) {
        this.startIdleTimer();
        return;
      }
      this.handleSessionLocked();
    }, remainingMs);
  }

  startRefreshTimer() {
    if (!this.refreshEnabled) return;
    const raw = sessionStorage.getItem("accessTokenExpiresAt");
    if (!raw) return;

    const expiresAt = new Date(raw).getTime();
    const delay = expiresAt - Date.now() - this.refreshBufferMs;

    if (delay <= 0) {
      this.refreshTokens().catch(() => {});
      return;
    }

    this.clearRefreshTimer();
    this.refreshTimer = setTimeout(() => {
      if (this.isUserActive()) {
        this.refreshTokens().catch(() => {});
      } else {
        this.handleSessionLocked();
      }
    }, delay);
  }

  isSessionExpiryError(error) {
    const status = Number(error?.response?.status || error?.response?.data?.code || 0);
    return status === 400 || status === 401 || status === 403;
  }

  scheduleRefreshRetry() {
    if (!this.refreshEnabled || !this.isUserActive() || this.isLocked) return;
    this.clearRefreshTimer();
    this.refreshTimer = setTimeout(() => {
      this.refreshTokens().catch(() => {});
    }, REFRESH_RETRY_DELAY_MS);
  }

  async refreshTokens() {
    if (this.isRefreshing && this.refreshPromise) return this.refreshPromise;

    this.isRefreshing = true;

    this.refreshPromise = (async () => {
      const refreshToken = sessionStorage.getItem("refreshToken");
      const clientId = localStorage.getItem("clientId");

      if (!refreshToken || !clientId) {
        this.handleSessionExpired();
        return;
      }

      try {
        const { data } = await apiClient.post(
          `${IDENTITY_SERVICE_BASE_URL}/api/v1/auth/token/refresh`,
          { refreshToken, deviceId: clientId }
        );

        if (!data?.accessToken) {
          this.handleSessionExpired();
          return;
        }

        sessionStorage.setItem("authToken", data.accessToken);
        sessionStorage.setItem("refreshToken", data.refreshToken);
        sessionStorage.setItem("accessTokenExpiresAt", data.accessTokenExpiresAt);
        sessionStorage.setItem("refreshTokenExpiresAt", data.refreshTokenExpiresAt);

        apiClient.defaults.headers.Authorization = `Bearer ${data.accessToken}`;

        if (this.isLocked) {
          this.unlockSession();
        }

        if (this.refreshEnabled) {
          this.startRefreshTimer();
        }

        this.onTokenRefreshed?.(data);
      } catch (error) {
        if (this.isSessionExpiryError(error)) {
          this.handleSessionExpired();
        } else {
          this.scheduleRefreshRetry();
        }
        throw error;
      }
    })().finally(() => {
      this.isRefreshing = false;
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  }

  clearRefreshTimer() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  clearIdleTimer() {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  clearLockTimer() {
    if (this.lockTimer) {
      clearTimeout(this.lockTimer);
      this.lockTimer = null;
    }
  }

  handleSessionLocked(lockedAt = Date.now()) {
    if (this.isLocked) {
      return;
    }

    if (this.getLastActivityTime() > lockedAt && this.isUserActive()) {
      this.unlockSession({ broadcast: true });
      return;
    }

    this.isLocked = true;
    this.lockedAt = lockedAt;
    this.clearIdleTimer();
    this.clearRefreshTimer();
    localStorage.setItem(SESSION_LOCK_STORAGE_KEY, String(lockedAt));

    const expiresAt = lockedAt + this.lockGraceMs;
    this.onSessionLocked?.({ lockedAt, expiresAt });
  }

  async keepSessionActive() {
    await this.refreshTokens();
    this.unlockSession();
  }

  unlockSession({ broadcast = true } = {}) {
    if (!this.isLocked) {
      this.recordActivity({ forceBroadcast: true });
      return;
    }

    this.isLocked = false;
    this.lockedAt = null;
    this.clearLockTimer();
    if (broadcast) {
      localStorage.setItem(SESSION_UNLOCK_STORAGE_KEY, String(Date.now()));
    }
    localStorage.removeItem(SESSION_LOCK_STORAGE_KEY);
    this.recordActivity({ forceBroadcast: true });
    this.onSessionUnlocked?.();
    if (this.refreshEnabled) {
      this.startRefreshTimer();
    }
  }

  handleSessionExpired() {
    this.cleanup();
    this.onSessionExpired?.();
  }

  cleanup() {
    this.clearRefreshTimer();
    this.clearIdleTimer();
    this.clearLockTimer();
    this.cleanupActivity?.();
  }
}

export default new TokenRefreshService();
